import type { LockScreenTexts } from "@ews-admin/global-design-system";
import type { TFunction } from "i18next";

/** Shared by the lock screen and the "change my PIN" flow (MED-1620). */
export const IDLE_LOCK_STORAGE_KEY = "medecine360-ui-idle-lock";

/** Lock delay of a session without `pinLockMinutes` (MED-1642). */
export const DEFAULT_PIN_LOCK_MINUTES = 15;
/** Lock delays the user can choose, in minutes. */
export const PIN_LOCK_MINUTES_OPTIONS = [5, 15, 30, 60] as const;

/** Forgets the locked flag kept by useIdleLock (it survives a reload). */
export const clearPersistedPinLock = () => {
  try {
    localStorage.removeItem(IDLE_LOCK_STORAGE_KEY);
  } catch {
    // Storage unavailable: nothing to clear.
  }
};

/**
 * A `sessionToken` in the URL means a fresh sign-in from the login app: the
 * screen must not open locked by the previous session. Run before the PIN lock
 * mounts, since useIdleLock reads the flag when it starts. A plain reload of a
 * locked tab (no token) stays locked.
 */
export const clearPinLockOnFreshLogin = (search: string) => {
  if (new URLSearchParams(search).get("sessionToken")) clearPersistedPinLock();
};

type PinError = { code?: string; data?: { remainingAttempts?: unknown } };

export const pinErrorCode = (error: unknown): string | undefined =>
  (error as PinError | null)?.code;

/** Translated message of a failed PIN call. */
export const pinErrorMessage = (t: TFunction, error: unknown): string => {
  const pinError = (error ?? {}) as PinError;
  switch (pinError.code) {
    case "PIN_INVALID": {
      const remaining = pinError.data?.remainingAttempts;
      return typeof remaining === "number"
        ? t("pinLock.errors.invalidWithAttempts", { remaining })
        : t("pinLock.errors.invalid");
    }
    case "PIN_INVALID_FORMAT":
      return t("pinLock.errors.invalidFormat");
    case "PIN_RESET_OTP_INVALID":
      return t("pinLock.errors.otpInvalid");
    case "PIN_RESET_RATE_LIMITED":
      return t("pinLock.errors.resetRateLimited");
    default:
      return t("pinLock.errors.generic");
  }
};

/** Every text of the design-system LockScreen, translated. */
export const lockScreenTexts = (
  t: TFunction,
  otpDescription?: string
): LockScreenTexts => ({
  dialogLabel: t("pinLock.dialogLabel"),
  createTitle: t("pinLock.createTitle"),
  confirmTitle: t("pinLock.confirmTitle"),
  unlockTitle: t("pinLock.unlockTitle"),
  currentTitle: t("pinLock.currentTitle"),
  newTitle: t("pinLock.newTitle"),
  otpTitle: t("pinLock.otpTitle"),
  otpDescription,
  otpLabel: t("pinLock.otpLabel"),
  otpSubmit: t("pinLock.otpSubmit"),
  mismatchError: t("pinLock.errors.mismatch"),
  forgot: t("pinLock.forgot"),
  cancel: t("pinLock.cancel"),
  signOut: t("pinLock.signOut"),
  erase: t("pinLock.erase"),
  keypad: t("pinLock.keypad"),
  progress: (filled: number, total: number) =>
    t("pinLock.progress", { filled, total }),
  digit: (position: number, total: number) =>
    t("pinLock.digit", { position, total }),
});
